import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '../../environments/environment';
import { User } from '../_models/user';
import { UserService } from '../admin/_services/user.service';
import { AMSClient } from '../_models/amsClient';
import { NotifyService } from './notify.service';

@Injectable({
   providedIn: 'root',
})
export class AuthService {
   baseUrl = environment.apiUrl + 'auth/';
   jwtHelper = new JwtHelperService();
   decodedToken: any;
   currentUser: User;
   isLoggedIn = false;
   photoUrl = new BehaviorSubject<string>('');
   loggedInNotify = new BehaviorSubject<boolean>(false);
   loggedOutNotify = new BehaviorSubject<boolean>(false);
   currentPhotoUrl = this.photoUrl.asObservable();

   constructor(private http: HttpClient, private userService: UserService, private notify: NotifyService) {}

   changeMemberPhoto(photoUrl: string) {
      this.photoUrl.next(photoUrl);
   }

   portallogin(model: any) {
      if (model) {
         localStorage.setItem('token', model.backend.token);
         localStorage.setItem('user', JSON.stringify(model.user));

         localStorage.setItem('isReset', 'false');
         this.currentUser = model.user;
         this.decodedToken = this.jwtHelper.decodeToken(model.backend.token);

         this.changeMemberPhoto(this.currentUser.url);
         this.isLoggedIn = true;
         this.loggedInNotify.next(true);
      }
   }

   login(model: any) {
      return this.http.post(this.baseUrl + 'login', model).pipe(
         map((response: any) => {
            const user = response;
            if (user) {
               localStorage.setItem('token', user.token);
               localStorage.setItem('user', JSON.stringify(user.user));
               localStorage.setItem('isReset', JSON.stringify(user.isPasswordReset));
               this.currentUser = user.user;

               this.decodedToken = this.jwtHelper.decodeToken(user.token);
               this.checkExpiration();
               this.changeMemberPhoto(this.currentUser.url);
               this.isLoggedIn = true;
               this.loggedInNotify.next(true);
            }
         })
      );
   }
   logout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      this.decodedToken = null;
      this.currentUser = null;
      this.loggedOutNotify.next(true);
   }
   register(user: User) {
      return this.http.post(this.baseUrl + 'register', user);
   }

   registerClient(client: AMSClient) {
      return this.http.post(environment.apiUrl + 'AMSClient', client);
   }
   hasTokenExpired() {
      const isReset = localStorage.getItem('isReset');
      const token = localStorage.getItem('token');
      if (isReset === 'true') {
         return 'isReset';
      }

      if (token) {
         if (this.jwtHelper.isTokenExpired(token)) {
            return 'expired';
         }
      } else {
         return null;
      }
   }

   roleMatch(allowedRoles): boolean {
      let isMatch = false;
      const token = localStorage.getItem('token');
      const userString = localStorage.getItem('user');
      const user = JSON.parse(userString) as User;
      if (this.jwtHelper.isTokenExpired(token)) {
         return false;
      }
      if (this.decodedToken) {
         const userRoles = user.permissions as Array<string>;
         if (!userRoles) {
            return isMatch;
         }
         allowedRoles.forEach((element: string) => {
            if (Array.isArray(userRoles)) {
               if (userRoles.includes(element)) {
                  isMatch = true;
                  return isMatch;
               }
            } else {
               if (userRoles === element) {
                  isMatch = true;
                  return isMatch;
               }
            }
         });
      }
      return isMatch;
   }
   checkMigration(): Observable<MigrationMessage> {
      return this.http.get<MigrationMessage>(environment.apiUrl + 'auth/migrationcheck');
   }

   checkSiteExpired(): Observable<number> {
      return this.http.get<number>(environment.apiUrl + 'AMSClient/check');
   }
   checkExpiration() {
      if (this.decodedToken) {
         const expiryDate = this.getTokenExpirationDate(this.decodedToken);
         const source = timer(expiryDate.getTime() - new Date().getTime());
         const abc = source.subscribe((val) => {
            let loginVar = this.hasTokenExpired();
            if (loginVar !== null) {
               if (loginVar === 'isReset') {
                  this.logout();
               } else if (loginVar === 'expired') {
                  this.logout();
                  this.notify.warning('Your session has expired and have been logged out.');
               }
            }
         });
      }
   }
   getTokenExpirationDate(token: string): Date {
      if (this.decodedToken.exp === undefined) {
         return null;
      }

      const date = new Date(0);
      date.setUTCSeconds(this.decodedToken.exp);
      return date;
   }
}

export interface MigrationMessage {
   message: string;
}
